import { LitElement, html, css } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js'; 

// SVG del icono 'chevron-down' de Lucide para usar directamente en el template
const CHEVRON_DOWN_SVG = html`
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-chevron-down">
        <path d="m6 9 6 6 6-6"/>
    </svg>
`;

export class ProgramViewer extends LitElement {
    
    static properties = {
        planContent: { 
            type: Array, 
            attribute: 'data-plan-content',
            converter: {
                fromAttribute: (value) => {
                    try {
                        return JSON.parse(value || '[]');
                    } catch (e) {
                        console.error("Error al parsear el atributo 'data-plan-content':", e);
                        return [];
                    }
                },
            }
        },
    };

    static styles = css`
        :host {
            display: block;
            font-family: Arial, sans-serif;
        }
        .program-container {
            padding: 20px;
            border-radius: 6px;
        }
        
        /* --------------------------------- */
        /* Estilos Base del Acordeón */
        /* --------------------------------- */
        .plan-accordion {
            margin-top: 20px;
        }
        details {
            border: 1px solid #ddd;
            border-radius: 20px;
            margin-bottom: 10px;
            overflow: hidden;
            background-color: #fff;
        }
        summary {
            font-weight: bold;
            padding: 15px;
            cursor: pointer;
            outline: none;
            transition: background-color 0.2s;
            
            /* FIX: Asegura Flexbox en todos los summaries */
            display: flex; 
            align-items: center;
            position: relative;
        }
        
        /* Oculta el marcador de flecha nativo de details/summary */
        summary::-webkit-details-marker,
        summary::marker {
            display: none; 
        }

        /* --------------------------------- */
        /* Estilos para el Acordeón EXTERIOR (Planes) */
        /* --------------------------------- */
        .multi-plan-view details > summary {
            background-color: #f0f0f0; 
            color: #333;
        }
        .multi-plan-view details[open] > summary {
            background-color: #8ea2ce; 
            color: #fff;
        }
        
        /* --------------------------------- */
        /* Estilos para el Acordeón INTERIOR (Temas) */
        /* --------------------------------- */
        .theme-accordion details > summary {
            background-color: #fff; 
            /* FIX: Aseguramos el color de la letra/stroke para el nivel interno */
            color: #333; 
            border-bottom: 1px solid #eee;
            padding: 10px 15px; 
            font-weight: normal;
        }
        .theme-accordion details[open] > summary {
            background-color: #f7f7f7; 
            color: #8ea2ce; /* Color azul para el título del tema abierto */
        }

        /* ------------------ */
        /* Índice Numerado (1)*/
        /* ------------------ */
        .summary-index {
            display: grid;
            place-items: center;
            width: 25px; 
            height: 25px;
            border-radius: 50%;
            background-color: #8ea2ce; 
            color: #fff;
            font-size: 0.8em;
            margin-right: 15px;
            flex-shrink: 0; 
            transition: all 0.2s;
        }

        /* ------------------ */
        /* Ícono Chevron (SVG) */
        /* ------------------ */
        .summary-title {
             flex-grow: 1; 
             margin-right: 10px; 
        }

        .summary-chevron {
            margin-left: auto; 
            transition: transform 0.3s;
            line-height: 1; 
            padding-left: 5px;
            flex-shrink: 0;
            display: flex; 
        }
        
        .summary-chevron svg {
            width: 20px; 
            height: 20px;
            transition: inherit; 
            /* FIX: Aseguramos que el color base del stroke sea gris oscuro */
            stroke: #333;
        }

        /* La regla de rotación es la clave. */
        details[open] .summary-chevron svg {
            transform: rotate(180deg);
        }

        /* Reglas de COLOR del Chevron (Stroke) */

        /* 1. Plan Abierto (Chevron Exterior) -> Blanco */
        .multi-plan-view details[open] > summary .summary-chevron svg {
            stroke: #272727; 
        }
        
        /* 2. Tema Cerrado (Chevron Interior) -> Gris oscuro (ya cubierto por el default stroke: #333, pero lo hacemos explícito para mayor robustez) */
        .theme-accordion summary .summary-chevron svg {
             stroke: #333;
        }

        /* 3. Tema Abierto (Chevron Interior) -> Azul */
        .theme-accordion details[open] .summary-chevron svg {
            stroke: #8ea2ce; 
        }


        /* Contenido del desplegable */
        .accordion-content {
            padding: 15px;
            background-color: #fff;
            color: #333;
            border-top: 1px solid #eee;
        }
        .theme-accordion .accordion-content {
            padding: 10px 15px 10px 55px; 
            font-size: 0.9em;
            color: #555;
            border-top: none; 
        }
    `;

    constructor() {
        super();
        this.planContent = [];
    }

    renderFlexibleContent(section) {
        if (!section || section.acf_fc_layout !== 'tema') {
            return html``;
        }
        return html`
            <div class="flexible-section" data-layout="tema">
                <div class="section-description">
                    ${unsafeHTML(section.descripcion)}
                </div>
            </div>
        `;
    }

    renderThemeAccordion(planPost) {
        if (!planPost.sections || planPost.sections.length === 0) {
            return html`<p style="padding: 15px;">Este plan no tiene temas definidos.</p>`;
        }
        
        return html`
            <div class="theme-accordion">
                ${planPost.sections.map((section, index) => {
                    const itemIndex = index + 1; 

                    if (section.acf_fc_layout === 'tema') {
                        return html`
                            <details>
                                <summary>
                                    <span class="summary-index">U${itemIndex}</span>
                                    <span class="summary-title">${section.titulo}</span>
                                    <span class="summary-chevron">${CHEVRON_DOWN_SVG}</span> 
                                </summary>
                                <div class="accordion-content">
                                    ${this.renderFlexibleContent(section)}
                                </div>
                            </details>
                        `;
                    }
                    return html``; 
                })}
            </div>
        `;
    }

    renderSinglePlanView(planPost) {
        return html`
            <div class="plan-accordion single-plan-view">
                <h2>${planPost.title}</h2>
                ${this.renderThemeAccordion(planPost)}
            </div>
        `;
    }

    renderMultiPlanView() {
        return html`
            <div class="program-container multi-plan-view">
                <h2>Planes de Estudio Disponibles</h2>
                <div class="plan-accordion">
                    ${this.planContent.map((planPost, index) => html`
                        <details ?open=${index === 0}>
                            <summary>
                                <span class="summary-title">${planPost.title}</span>
                                <span class="summary-chevron">${CHEVRON_DOWN_SVG}</span> 
                            </summary>
                            <div class="accordion-content">
                                ${this.renderThemeAccordion(planPost)}
                            </div>
                        </details>
                    `)}
                </div>
            </div>
        `;
    }

    render() {
        if (!this.planContent || this.planContent.length === 0) {
            return html`<div class="program-container"><p>No se encontró el Plan de Estudio.</p></div>`;
        }

        const isSinglePlanPost = this.planContent.length === 1;
        
        if (isSinglePlanPost) {
            return this.renderSinglePlanView(this.planContent[0]);
        } else {
            return this.renderMultiPlanView();
        }
    }
}

customElements.define('mawc-program-viewer', ProgramViewer);